import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Tag Link variants — compact clickable tag footprint from Badge `tag`,
 * with rest / focus-visible / disabled states and opt-in hover and pressed tints.
 * Each color tints the background with a light shade of its own text color.
 * By default hover and press keep the rest colors and only the pointer cursor
 * signals it is clickable; `darkenOnHover` opts into a deeper tint.
 * Primary, neutral, and success reuse Badge's brand, neutral, and success tone
 * tokens so a Tag Link and a Badge of the same tone read the same. Badge has
 * no secondary tone, so secondary uses the secondary palette directly.
 */
export const tagLinkVariants = cva(
  [
    'inline-flex max-w-fit items-center gap-1 rounded-badge font-badge px-badge-x py-badge-y text-badge-label font-body not-italic',
    'border-none border-0',
    'transition-colors duration-200',
    'focus:!outline-none focus:!shadow-none !ring-0 !ring-offset-0',
    'focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-focus',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none',
    'aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:pointer-events-none',
    '[&_svg]:!size-badge-icon [&_[data-hen-icon]]:!size-badge-icon',
  ],
  {
    variants: {
      variant: {
        secondary: 'bg-secondary-50 text-secondary-700',
        primary: 'bg-badge-brand-subtle-bg text-badge-brand-subtle-text',
        neutral: 'bg-badge-neutral-subtle-bg text-badge-neutral-subtle-text',
        success: 'bg-badge-success-subtle-bg text-badge-success-subtle-text',
      },
      size: {
        sm: '',
        lg: 'px-xs !text-l1 !leading-l1',
      },
      /** False when Tag Link renders as a plain label with no pointer or pressed cues. */
      interactive: {
        true: 'cursor-pointer',
        false: 'cursor-default',
      },
      /** Deeper tint of the same color on hover and press. */
      darkenOnHover: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        interactive: true,
        darkenOnHover: true,
        variant: 'secondary',
        className:
          'hover:bg-secondary-100 hover:text-secondary-900 active:bg-secondary-100 active:text-secondary-900',
      },
      {
        interactive: true,
        darkenOnHover: true,
        variant: 'primary',
        className:
          'hover:bg-badge-brand-strong-bg hover:text-badge-brand-strong-text active:bg-badge-brand-strong-bg active:text-badge-brand-strong-text',
      },
      {
        interactive: true,
        darkenOnHover: true,
        variant: 'neutral',
        className:
          'hover:bg-badge-neutral-strong-bg hover:text-badge-neutral-strong-text active:bg-badge-neutral-strong-bg active:text-badge-neutral-strong-text',
      },
      {
        interactive: true,
        darkenOnHover: true,
        variant: 'success',
        className:
          'hover:bg-badge-success-strong-bg hover:text-badge-success-strong-text active:bg-badge-success-strong-bg active:text-badge-success-strong-text',
      },
    ],
    defaultVariants: {
      variant: 'secondary',
      size: 'sm',
      interactive: true,
      darkenOnHover: false,
    },
  },
);

export type TagLinkVariantProps = Omit<
  VariantProps<typeof tagLinkVariants>,
  'interactive'
>;
